import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { KeyRound, Sprout, ArrowLeft, ShieldCheck } from "lucide-react";

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const forced = !!user?.force_password_change;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOk("");
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("New password and confirmation do not match."); return; }
    if (next === current) { setError("Pick a password different from your current one."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setOk("Password updated. Redirecting…");
      const u = await refresh();
      setTimeout(() => {
        if (u?.role === "super_admin" && !u?.is_impersonating) navigate("/admin");
        else if (u?.role === "owner") navigate("/horses");
        else navigate("/dashboard");
      }, 700);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#F5EFE6" }} data-testid="change-password-page">
      <form onSubmit={submit} className="se-card w-full max-w-md" data-testid="change-password-form">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#7C5C3A", color: "#F5EFE6" }}>
            {forced ? <ShieldCheck size={20} /> : <KeyRound size={20} />}
          </div>
          <div>
            <div className="se-label">{forced ? "Required" : "Account"}</div>
            <h1 className="font-heading mt-1" style={{ fontSize: 22 }}>
              {forced ? "Set a new password to continue" : "Change your password"}
            </h1>
          </div>
        </div>
        {forced ? (
          <p className="text-sm mb-5" style={{ color: "#8C9E8A" }}>
            An administrator has required you to update your password before you can access the app.
          </p>
        ) : (
          <p className="text-sm mb-5" style={{ color: "#8C9E8A" }}>
            We never store or display your password. Use at least 8 characters.
          </p>
        )}

        <label className="se-label block mb-1">Current password</label>
        <input type="password" className="se-input mb-3" required autoComplete="current-password"
          value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" />

        <label className="se-label block mb-1">New password</label>
        <input type="password" className="se-input mb-3" required autoComplete="new-password"
          value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new"
          minLength={8} placeholder="At least 8 characters" />

        <label className="se-label block mb-1">Confirm new password</label>
        <input type="password" className="se-input mb-3" required autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm"
          minLength={8} />

        {error && (
          <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="cp-error">
            {error}
          </div>
        )}
        {ok && (
          <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(74,124,89,0.15)", color: "#2D5E3A" }} data-testid="cp-success">
            {ok}
          </div>
        )}

        <button type="submit" className="se-btn w-full" disabled={busy} data-testid="cp-submit">
          <Sprout size={14} /> {busy ? "Updating…" : forced ? "Update password and continue" : "Save new password"}
        </button>

        {!forced && (
          <button type="button" className="se-btn se-btn-ghost w-full mt-2" onClick={() => navigate(-1)} data-testid="cp-cancel">
            <ArrowLeft size={14} /> Cancel
          </button>
        )}
      </form>
    </div>
  );
}
